import { memo } from 'react'
import { Theme, Typography } from '@mui/material'
import { observer } from 'mobx-react'

import useEmotionStyles from 'hooks/useEmotionStyles'
import { AgeGroup } from 'constants/components'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import useUserState from 'hooks/useUserState'

interface MotivationalTextBelowProps {
  percentage: number
  label?: string
}

const LAST_STEP_TEXT = 'Letzter Schritt!'
const TWO_STEPS_LEFT_TEXT = 'Nur noch 2 Schritte'
const THREE_STEPS_LEFT_TEXT = 'Nur noch 3 Schritte'

const PAGE_WITH_CUSTOM_TEXT = {
  rdi: TWO_STEPS_LEFT_TEXT,
  activeLoans: THREE_STEPS_LEFT_TEXT,
  vehicle: THREE_STEPS_LEFT_TEXT,
  payoutWithoutDac: TWO_STEPS_LEFT_TEXT,
  dacSmavaHard: LAST_STEP_TEXT,
  verifiedPin: LAST_STEP_TEXT
}

const getMessage = (
  percentage: number,
  currentPageInSession: string | null,
  isCarLoanPurpose: boolean,
  label?: string,
  overrideCustomText?: string | undefined
): string => {
  if (overrideCustomText) {
    return overrideCustomText
  }

  if (
    currentPageInSession === 'payoutWithoutDac' ||
    currentPageInSession === 'rdi' ||
    currentPageInSession === 'dacSmavaHard' ||
    (currentPageInSession === 'activeLoans' && !isCarLoanPurpose) ||
    (currentPageInSession === 'vehicle' && isCarLoanPurpose) ||
    currentPageInSession === 'verifiedPin'
  ) {
    return PAGE_WITH_CUSTOM_TEXT[currentPageInSession]
  }

  if (percentage) {
    return `${percentage} % ${label || 'geschafft'}`
  }
  return ''
}

const MotivationalTextBelow = observer(
  ({ percentage, label }: MotivationalTextBelowProps) => {
    const { motivationalTextBelow } = useEmotionStyles(styles)

    const {
      rootStore: {
        navigation: { currentPageInSession },
        traversal: {
          data: {
            common: { purpose }
          }
        }
      }
    } = useRootStore()

    const { ageGroup, isPremium } = useUserState()

    const isDacCtaFlowEnabled = !(
      ageGroup === AgeGroup.MIDDLE_AGE_GROUP && !isPremium
    )

    const getSmavaMiddleAgeCustomText = (
      isInMiddleOrYoungAgeGroup: boolean,
      currentPage: string | null,
      currentPercent: number
    ): string | undefined => {
      if (currentPageInSession === 'verifiedPin') {
        return PAGE_WITH_CUSTOM_TEXT[currentPageInSession]
      }
      if (isInMiddleOrYoungAgeGroup && currentPage === 'dac') {
        return LAST_STEP_TEXT
      }
      if (isDacCtaFlowEnabled && isPremium && currentPage === 'payoutAccount') {
        return LAST_STEP_TEXT
      }
      if (isInMiddleOrYoungAgeGroup && currentPage === 'payoutAccount') {
        return TWO_STEPS_LEFT_TEXT
      }
      if (isInMiddleOrYoungAgeGroup && currentPercent) {
        return `${currentPercent} % geschafft`
      }
      return undefined
    }

    const overrideCustomText = getSmavaMiddleAgeCustomText(
      ageGroup === AgeGroup.MIDDLE_AGE_GROUP ||
        ageGroup === AgeGroup.YOUNG_AGE_GROUP,
      currentPageInSession,
      percentage
    )

    const isCarLoanPurpose =
      purpose === 'PRE_OWNED_CAR' || purpose === 'NEW_CAR'

    const message = getMessage(
      percentage,
      currentPageInSession,
      isCarLoanPurpose,
      label,
      overrideCustomText
    )

    if (!message) {
      return null
    }

    return (
      <Typography variant="caption" className={motivationalTextBelow}>
        {message}
      </Typography>
    )
  }
)

export default memo(MotivationalTextBelow)

const styles = (theme: Theme) => ({
  motivationalTextBelow: {
    color: theme.palette.grey[800]
  }
})
